/**
 * Bengali Language Profile
 *
 * SOV word order, postpositions (কে, তে, থেকে, etc.), Bengali script.
 * Agglutinative language similar to Hindi.
 */

import type { LanguageProfile } from './types';

export const bengaliProfile: LanguageProfile = {
  code: 'bn',
  name: 'Bengali',
  nativeName: 'বাংলা',
  direction: 'ltr',
  script: 'bengali',
  wordOrder: 'SOV',
  markingStrategy: 'postposition',
  usesSpaces: true,
  defaultVerbForm: 'imperative',
  verb: {
    position: 'end',
    suffixes: ['ুন', 'ো', 'া', 'ে', 'ি'],
    subjectDrop: true,
  },
  references: {
    me: 'আমি',
    it: 'এটি',
    you: 'আপনি',
    result: 'ফলাফল',
    event: 'ঘটনা',
    target: 'লক্ষ্য',
    body: 'বডি',
    document: 'ডকুমেন্ট',
    window: 'উইন্ডো',
  },
  // A pronoun and its marker as Bengali writes them (M2 N3; listed in
  // NATIVE_REVIEW_NEEDED.md): fused (`আমাকে`), or the genitive before `থেকে`.
  // The reader splits a fused word back (tokenizers/fused-forms-split.ts), to
  // `এ` where `তে` also writes it (`তে` is also the event marker).
  obliqueReferences: {
    me: { কে: 'আমাকে', তে: 'আমাতে', থেকে: 'আমার থেকে', এ: 'আমাতে' },
    it: { কে: 'এটিকে', তে: 'এটিতে', এ: 'এটিতে' },
  },
  // A query's scope before it (`#list এ থাকা <li/>`, `আমাতে থাকা <p/>`; M2 N3).
  queryScopeFirst: { marker: 'এ', participle: 'থাকা', queryIn: 'এ' },
  possessive: {
    marker: 'র',
    markerPosition: 'between',
    keywords: {
      // "my" - আমার (amar)
      আমার: 'me',
      // "your" - তোমার (tomar, informal), আপনার (apnar, formal)
      তোমার: 'you',
      আপনার: 'you',
      // "its/his/her" - তার (tar), এর (er)
      তার: 'it',
      এর: 'it',
    },
  },
  roleMarkers: {
    patient: { primary: 'কে', position: 'after' },
    destination: { primary: 'তে', alternatives: ['এ'], position: 'after' },
    source: { primary: 'থেকে', position: 'after' },
    style: { primary: 'দিয়ে', position: 'after' },
    event: { primary: 'তে', position: 'after' },
  },
  keywords: {
    // Class/Attribute operations
    // `টগল করুন` (toggle, polite): the hand-crafted pattern's own form, which a
    // handler body read as `টগল` and an unread `করুন`. Read-only (an alternative).
    toggle: { primary: 'টগল', alternatives: ['টগল করুন'], normalized: 'toggle' },
    add: { primary: 'যোগ', alternatives: ['যোগ করুন'], normalized: 'add' },
    remove: { primary: 'সরান', alternatives: ['সরিয়ে ফেলুন', 'মুছুন'], normalized: 'remove' },
    // Content operations
    put: { primary: 'রাখুন', alternatives: ['রাখ'], normalized: 'put' },
    // জুড়ুন (attach/join, imperative — matches the রাখুন/নিন verb style).
    // The old multi-word 'শেষে যোগ' could never tokenize as one keyword
    // (শেষে reads as `end`, যোগ as `add`), so bn append always parsed as add.
    append: { primary: 'জুড়ুন', alternatives: [], normalized: 'append' },
    prepend: { primary: 'শুরুতে যোগ', alternatives: [], normalized: 'prepend' },
    take: { primary: 'নিন', alternatives: ['নে'], normalized: 'take' },
    make: { primary: 'তৈরি করুন', alternatives: ['বানান'], normalized: 'make' },
    clone: { primary: 'ক্লোন', alternatives: ['প্রতিলিপি'], normalized: 'clone' },
    swap: { primary: 'বদল', alternatives: [], normalized: 'swap' },
    morph: { primary: 'রূপান্তর', alternatives: [], normalized: 'morph' },
    // Variable operations
    set: { primary: 'সেট', alternatives: ['নির্ধারণ'], normalized: 'set' },
    get: { primary: 'পান', normalized: 'get' },
    // `বৃদ্ধি করুন` / `হ্রাস করুন`: the hand-crafted patterns' polite forms, read
    // whole (a handler body took `করুন` for the patient). Read-only alternatives.
    increment: {
      primary: 'বৃদ্ধি',
      alternatives: ['বাড়ান', 'বৃদ্ধি করুন'],
      normalized: 'increment',
    },
    decrement: { primary: 'হ্রাস', alternatives: ['কমান', 'হ্রাস করুন'], normalized: 'decrement' },
    log: { primary: 'লগ', alternatives: ['রেকর্ড'], normalized: 'log' },
    // Visibility
    show: { primary: 'দেখান', alternatives: ['দেখাও'], normalized: 'show' },
    hide: { primary: 'লুকান', alternatives: ['লুকাও'], normalized: 'hide' },
    transition: { primary: 'সংক্রমণ', alternatives: [], normalized: 'transition' },
    // Events
    on: { primary: 'তে', alternatives: ['এ'], normalized: 'on' },
    trigger: { primary: 'ট্রিগার', alternatives: [], normalized: 'trigger' },
    send: { primary: 'পাঠান', alternatives: ['পাঠাও'], normalized: 'send' },
    // DOM focus
    focus: { primary: 'ফোকাস', alternatives: ['মনোযোগ'], normalized: 'focus' },
    blur: { primary: 'ঝাপসা', alternatives: ['ফোকাস_সরান'], normalized: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    empty: { primary: 'খালি-করুন', alternatives: ['খালি'], normalized: 'empty' },
    open: { primary: 'খুলুন', normalized: 'open' },
    close: { primary: 'বন্ধ', normalized: 'close' },
    select: { primary: 'নির্বাচন', normalized: 'select' },
    clear: { primary: 'পরিষ্কার', normalized: 'clear' },
    reset: { primary: 'রিসেট', normalized: 'reset' },
    breakpoint: { primary: 'ব্রেকপয়েন্ট', normalized: 'breakpoint' },
    // Common event names (for event handler patterns)
    click: { primary: 'ক্লিক', normalized: 'click' },
    hover: { primary: 'হোভার', alternatives: ['উপরে_রাখুন'], normalized: 'hover' },
    submit: { primary: 'সাবমিট', alternatives: ['জমা'], normalized: 'submit' },
    input: { primary: 'ইনপুট', alternatives: ['প্রবেশ'], normalized: 'input' },
    change: { primary: 'পরিবর্তন', normalized: 'change' },
    // Navigation
    go: { primary: 'যান', alternatives: ['যাও'], normalized: 'go' },
    scroll: { primary: 'স্ক্রোল', normalized: 'scroll' },
    push: { primary: 'পুশ', alternatives: ['ঠেলুন'], normalized: 'push' },
    replace: { primary: 'প্রতিস্থাপন', normalized: 'replace' },
    process: { primary: 'প্রসেস', alternatives: ['প্রক্রিয়া'], normalized: 'process' },
    // Async
    wait: { primary: 'অপেক্ষা', normalized: 'wait' },
    fetch: { primary: 'আনুন', alternatives: [], normalized: 'fetch' },
    settle: { primary: 'স্থির', alternatives: [], normalized: 'settle' },
    // Control flow
    if: { primary: 'যদি', alternatives: [], normalized: 'if' },
    // "যদি না" ("if not") — the standard Bengali negated conditional. The
    // SPACED multi-word form, like hi's `जब तक नहीं` and qu's `mana sichus`,
    // so the tokenizer fuses it ahead of the bare `যদি` (longest match). bn
    // was the ONLY profile without an unless keyword, so the generator built
    // no unless pattern and render(unless, bn) fell to the explicit
    // `[unless condition:…]` placeholder, which nothing parses back.
    unless: { primary: 'যদি না', normalized: 'unless' },
    when: { primary: 'যখন', normalized: 'when' },
    // Reactive `when <expr> changes` trigger word — synced VERBATIM from the i18n
    // dictionary (`changes`), which is what wrote every stored corpus row; the V1
    // vocab gate requires the two surfaces to agree. Native review pending — see
    // NATIVE_REVIEW_NEEDED.md § "Reactive `when … changes`".
    changes: { primary: 'পরিবর্তিত হলে', normalized: 'changes' },
    where: { primary: 'কোথায়', normalized: 'where' },
    else: { primary: 'নতুবা', alternatives: ['না হলে'], normalized: 'else' },
    repeat: { primary: 'পুনরাবৃত্তি', alternatives: ['বার বার'], normalized: 'repeat' },
    for: { primary: 'জন্য', alternatives: [], normalized: 'for' },
    while: { primary: 'যতক্ষণ', alternatives: [], normalized: 'while' },
    // `repeat forever` loop keyword — corpus word recognized so loopType types
    // as `:literal` like EN (the repeat.loopType R1 residue; see spanish.ts).
    forever: { primary: 'চিরকাল', normalized: 'forever', alternatives: ['forever'] },
    continue: { primary: 'চালিয়ে যান', alternatives: [], normalized: 'continue' },
    halt: { primary: 'থামুন', alternatives: ['থামাও'], normalized: 'halt' },
    throw: { primary: 'নিক্ষেপ', alternatives: ['ছুঁড়ে দিন'], normalized: 'throw' },
    call: { primary: 'কল', alternatives: ['ডাকুন'], normalized: 'call' },
    return: { primary: 'ফিরুন', alternatives: ['ফেরত দিন'], normalized: 'return' },
    then: { primary: 'তারপর', alternatives: ['তখন'], normalized: 'then' },
    and: { primary: 'এবং', alternatives: [], normalized: 'and' },
    // Copula (`if result is false`, `if my value is empty`). Without the keyword the
    // surface stays an identifier and leaks verbatim into the condition's raw
    // expression, which the core expression parser reads as English. Neither an
    // ActionType nor a command schema, so no pattern is generated from it.
    is: { primary: 'হয়', normalized: 'is' },
    end: { primary: 'শেষ', alternatives: ['সমাপ্ত'], normalized: 'end' },
    // Advanced
    js: { primary: 'জেএস', alternatives: ['js'], normalized: 'js' },
    async: { primary: 'অ্যাসিঙ্ক', alternatives: [], normalized: 'async' },
    tell: { primary: 'বলুন', alternatives: ['বল'], normalized: 'tell' },
    default: { primary: 'ডিফল্ট', alternatives: [], normalized: 'default' },
    init: { primary: 'শুরু', alternatives: [], normalized: 'init' },
    behavior: { primary: 'আচরণ', alternatives: [], normalized: 'behavior' },
    install: { primary: 'ইনস্টল', alternatives: [], normalized: 'install' },
    measure: { primary: 'মাপুন', alternatives: [], normalized: 'measure' },
    beep: { primary: 'বীপ', alternatives: [], normalized: 'beep' },
    ask: { primary: 'জিজ্ঞাসা', normalized: 'ask' },
    answer: { primary: 'উত্তর', normalized: 'answer' },
    break: { primary: 'ভাঙুন', alternatives: [], normalized: 'break' },
    copy: { primary: 'কপি', alternatives: [], normalized: 'copy' },
    exit: { primary: 'বের', alternatives: [], normalized: 'exit' },
    pick: { primary: 'বাছুন', alternatives: [], normalized: 'pick' },
    render: { primary: 'রেন্ডার', alternatives: [], normalized: 'render' },
    // Modifiers
    into: { primary: 'ভিতরে', normalized: 'into' },
    before: { primary: 'আগে', alternatives: [], normalized: 'before' },
    after: { primary: 'পরে', alternatives: [], normalized: 'after' },
    until: { primary: 'পর্যন্ত', alternatives: [], normalized: 'until' },
    event: { primary: 'ঘটনা', alternatives: [], normalized: 'event' },
    from: { primary: 'থেকে', normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-কানেক্ট`, `hx-লাইভ`, etc.
    connect: { primary: 'কানেক্ট', alternatives: ['সংযোগ', 'যুক্ত'], normalized: 'connect' },
    stream: { primary: 'স্ট্রিম', alternatives: ['স্রোত', 'প্রবাহ'], normalized: 'stream' },
    live: { primary: 'লাইভ', alternatives: ['সরাসরি', 'প্রত্যক্ষ'], normalized: 'live' },
    socket: { primary: 'সকেট', alternatives: ['ওয়েবসকেট'], normalized: 'socket' },
    // Reactive / realtime commands
    // `যুক্ত` collides with connect; keep the loan `বাইন্ড` + English form.
    bind: { primary: 'বাইন্ড', alternatives: ['bind'], normalized: 'bind' },
    intercept: {
      primary: 'আটকাও',
      alternatives: ['ইন্টারসেপ্ট', 'intercept'],
      normalized: 'intercept',
    },
    worker: { primary: 'কর্মী', alternatives: ['ওয়ার্কার', 'worker'], normalized: 'worker' },
    eventsource: {
      primary: 'eventsource',
      alternatives: ['ইভেন্টসোর্স'],
      normalized: 'eventsource',
    },
  },
  tokenization: {
    particles: ['কে', 'তে', 'থেকে', 'র', 'এর', 'দিয়ে', 'জন্য', 'পর্যন্ত'],
    boundaryStrategy: 'space',
  },
  // Bengali writes a case ending onto its noun (`#outকে`, `#outএ`; M2 N5).
  // After a Bengali word only `কে`, which never changes: the locative is `তে`
  // after a vowel and the vowel sign `ে` after a consonant (`ক্লিকে`), which
  // the reader does not split.
  attachedMarkers: { markers: ['কে', 'তে', 'এ'], afterWords: ['কে'] },
  eventHandler: {
    keyword: { primary: 'তে', alternatives: ['এ', 'যখন'], normalized: 'on' },
    sourceMarker: { primary: 'থেকে', position: 'after' },
    // Event marker: তে (at/on), used in SOV pattern
    // Pattern: [event] তে [destination র?] [patient] কে [action]
    // Example: ক্লিক তে #button র .active কে টগল
    eventMarker: { primary: 'তে', alternatives: ['এ'], position: 'after' },
    temporalMarkers: ['যখন', 'যখনই'], // temporal conjunctions (when, whenever)
  },
};
